import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getReferrals, isAuthenticated } from '@/lib/memberAuth';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import {
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  FileText,
  Calendar,
} from 'lucide-react';

interface Referral {
  id: number;
  referral_number: string;
  status: string;
  urgency_level: string;
  primary_diagnosis?: string;
  referral_reasons?: string;
  created_at: string;
  updated_at: string;
  program_name?: string;
  acceptance_status?: string;
}

export default function MemberReferralsPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login');
      return;
    }

    loadReferrals();
  }, [navigate]);

  const loadReferrals = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getReferrals();
      setReferrals(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string; icon: any }> = {
      active: { label: 'Active', className: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
      pending: { label: 'Pending', className: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock },
      completed: { label: 'Completed', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle },
      cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
      in_progress: { label: 'In Progress', className: 'bg-teal-50 text-teal-700 border-teal-200', icon: Clock },
    };

    const config = statusConfig[status.toLowerCase()] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant="outline" className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getUrgencyBadge = (urgency: string) => {
    const urgencyConfig: Record<string, { label: string; className: string }> = {
      high: { label: 'High Priority', className: 'bg-red-100 text-red-800 border-red-300' },
      medium: { label: 'Medium Priority', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      low: { label: 'Low Priority', className: 'bg-paper-2 text-ink-700 border-v2border' },
      urgent: { label: 'Urgent', className: 'bg-red-100 text-red-800 border-red-300' },
    };

    const config = urgencyConfig[urgency.toLowerCase()] || urgencyConfig.medium;

    return (
      <Badge variant="outline" className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const pageHeader = (
    <PageHeader
      leading={
        <Link to="/member/dashboard">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('referrals.back', 'Back')}
          </Button>
        </Link>
      }
      title={t('referrals.my_referrals', 'My Referrals')}
      description={`${referrals.length} referral${referrals.length !== 1 ? 's' : ''}`}
    />
  );

  if (loading) {
    return (
      <>
        {pageHeader}
        <PageContainer size="wide">
          <PageState variant="loading" />
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {pageHeader}
      <PageContainer size="wide">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {referrals.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-ink-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-ink mb-2">{t('referrals.no_referrals_yet', 'No referrals yet')}</h3>
              <p className="text-ink-500">
                {t('referrals.you_dont_have_any_referrals', 'You don\'t have any referrals at the moment. Your case manager will create referrals for you when needed.')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {referrals.map((referral) => (
              <Card key={referral.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <CardTitle className="text-xl">
                          Referral #{referral.referral_number}
                        </CardTitle>
                        {getStatusBadge(referral.status)}
                        {referral.urgency_level && getUrgencyBadge(referral.urgency_level)}
                      </div>
                      {referral.program_name && (
                        <CardDescription className="text-base">
                          {t('referrals.program', 'Program:')} <span className="font-medium text-ink">{referral.program_name}</span>
                        </CardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {referral.primary_diagnosis && (
                    <div>
                      <Label className="text-sm font-medium text-ink-700">{t('referrals.primary_diagnosis', 'Primary Diagnosis')}</Label>
                      <p className="mt-1 text-ink">{referral.primary_diagnosis}</p>
                    </div>
                  )}

                  {referral.referral_reasons && (
                    <div>
                      <Label className="text-sm font-medium text-ink-700">{t('referrals.reason_for_referral', 'Reason for Referral')}</Label>
                      <p className="mt-1 text-ink">{referral.referral_reasons}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <Label className="text-sm font-medium text-ink-700 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {t('referrals.created', 'Created')}
                      </Label>
                      <p className="mt-1 text-ink">{formatDate(referral.created_at)}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-ink-700 flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {t('referrals.last_updated', 'Last Updated')}
                      </Label>
                      <p className="mt-1 text-ink">{formatDate(referral.updated_at)}</p>
                    </div>
                  </div>

                  {referral.acceptance_status && (
                    <div className="pt-4 border-t">
                      <Label className="text-sm font-medium text-ink-700">{t('referrals.acceptance_status', 'Acceptance Status')}</Label>
                      <p className="mt-1">
                        <Badge variant="outline">
                          {referral.acceptance_status.replace(/_/g, ' ').toUpperCase()}
                        </Badge>
                      </p>
                    </div>
                  )}

                  <div className="flex items-center space-x-3 pt-4">
                    <Link to={`/member/notes?referral_id=${referral.id}`}>
                      <Button variant="outline" size="sm">
                        <FileText className="w-4 h-4 mr-2" />
                        {t('referrals.add_note', 'Add Note')}
                      </Button>
                    </Link>
                    <Link to={`/member/documents?referral_id=${referral.id}`}>
                      <Button variant="outline" size="sm">
                        <FileText className="w-4 h-4 mr-2" />
                        {t('referrals.upload_document', 'Upload Document')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </PageContainer>
    </>
  );
}

function Label({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <label className={`block text-sm font-medium ${className}`}>{children}</label>;
}
